import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ImageSquare, ArrowSquareOut, ArrowsHorizontal, Rows, SquaresFour } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { devApi, docLink } from './devApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

/** 書類のサムネイル（画像はその場で読み込み、実体はDMSのまま）。 */
const Thumb = ({ doc, className = 'h-28 w-28', testid }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true; let objectUrl = null;
    axios.get(`${API}/documents/${doc.document_id}/file`, { responseType: 'blob' })
      .then((r) => { if (live) { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); } })
      .catch(() => {});
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc.document_id]);
  return (
    <Link to={docLink(doc.document_id, doc.page)} data-testid={testid}
      className={`group relative block overflow-hidden rounded-md border border-zinc-300 bg-zinc-100 ${className}`}
      title={doc.title}>
      {url ? (
        <img src={url} alt={doc.title || '写真'} className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-zinc-400">
          <ImageSquare size={22} />
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-zinc-900/70 px-1 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">
        {doc.title} <ArrowSquareOut size={9} className="inline" />
      </span>
    </Link>
  );
};

/** 試作の写真ギャラリー — 時系列 / サムネイル / 前後比較。 */
export const PhotoGallery = ({ devId, testid = 'dev-gallery' }) => {
  const [data, setData] = useState(null);
  const [view, setView] = useState('timeline');
  const [a, setA] = useState('');
  const [b, setB] = useState('');

  useEffect(() => { devApi.gallery(devId).then(setData).catch(() => {}); }, [devId]);
  if (!data) return null;

  const all = data.groups.flatMap((g) => g.photos.map((p) => ({ ...p, group: g.name })));
  const all_other = data.groups.flatMap((g) => g.other_evidence || []);
  const pick = (id) => all.find((x) => x.document_id === id);
  // ルール1: 写真も証跡も無いときは出さない。「写真はまだありません」は、すぐ上の
  // 「試作はまだ登録されていません」と同じことを別の言い方で繰り返すだけになる。
  if (data.photo_count === 0 && all_other.length === 0) return null;

  return (
    <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-300 bg-zinc-100 px-4 py-2">
        <p className="text-sm font-bold text-zinc-800">写真ギャラリー（{data.photo_count}枚）</p>
        <div className="ml-auto flex gap-1">
          {[['timeline', '時系列', Rows], ['grid', 'サムネイル', SquaresFour],
            ['compare', '前後比較', ArrowsHorizontal]].map(([v, label, Icon]) => (
            <Button key={v} size="sm" variant={view === v ? 'default' : 'outline'}
              className="h-7 px-2 text-xs" onClick={() => setView(v)}
              data-testid={`${testid}-view-${v}`}>
              <Icon size={12} className="mr-1" /> {label}
            </Button>
          ))}
        </div>
      </div>

      {view === 'timeline' ? (
        <ul className="divide-y divide-zinc-200" data-testid={`${testid}-timeline`}>
          {data.groups.filter((g) => g.photos.length > 0 || (g.other_evidence || []).length > 0).map((g) => (
            <li key={g.prototype_id} className="px-4 py-3">
              <p className="text-sm font-semibold text-zinc-900">
                {g.name} <span className="text-xs font-normal text-zinc-600">
                  製作日 {g.build_date || '—'} · 写真 {g.photos.length}枚
                </span>
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {g.photos.map((p) => (
                  <Thumb key={p.document_id} doc={p}
                    testid={`${testid}-photo-${p.document_id}`} />
                ))}
              </div>
              {(g.other_evidence || []).length > 0 && (
                <ul className="mt-2 space-y-0.5" data-testid={`${testid}-other-${g.prototype_id}`}>
                  {g.other_evidence.map((e) => (
                    <li key={e.document_id}>
                      <Link to={docLink(e.document_id, e.page)}
                        data-testid={`${testid}-doc-${e.document_id}`}
                        className="text-xs text-teal-800 underline">
                        その他の証跡: {e.title || '書類'} <ArrowSquareOut size={9} className="inline" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      ) : view === 'grid' ? (
        <div className="flex flex-wrap gap-2 px-4 py-3" data-testid={`${testid}-grid`}>
          {all.map((p) => (
            <Thumb key={p.document_id} doc={p} className="h-32 w-32"
              testid={`${testid}-photo-${p.document_id}`} />
          ))}
        </div>
      ) : (
        <div className="px-4 py-3" data-testid={`${testid}-compare`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {[[a, setA, 'before'], [b, setB, 'after']].map(([val, set, key], i) => (
              <div key={key}>
                <p className="mb-1 text-xs font-semibold text-zinc-700">
                  {i === 0 ? '前（Before）' : '後（After）'}
                </p>
                <Select value={val} onValueChange={set}>
                  <SelectTrigger data-testid={`${testid}-select-${key}`}>
                    <SelectValue placeholder="写真を選ぶ" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {all.map((p) => (
                      <SelectItem key={p.document_id} value={p.document_id}>
                        {p.group} · {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pick(val) && (
                  <div className="mt-2">
                    <Thumb doc={pick(val)} className="h-64 w-full"
                      testid={`${testid}-compare-${key}`} />
                    <p className="mt-1 text-xs text-zinc-600">{pick(val).group}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
